/**
 * Enhanced Select Field Component
 * A standalone MUI TextField wrapper with select mode and additional features:
 * - Clear button (allowClear)
 * - Loading indicator (loading)
 * - Change highlighting (enableChangeHighlight)
 * - Hover effects
 *
 * This component can be used independently without FormBuilder or react-hook-form
 */
import React, { useState } from 'react'
import {
  TextField,
  MenuItem,
  InputAdornment,
  IconButton,
  CircularProgress,
  TextFieldProps
} from '@mui/material'
import ClearIcon from '@mui/icons-material/Clear'

// Option type for select items
export interface SelectOption {
  value: string | number
  label: string
}

// Enhanced props extending MUI TextField props
export interface SelectFieldProps
  extends Omit<TextFieldProps, 'select' | 'onChange'> {
  // Enhanced features
  allowClear?: boolean
  loading?: boolean
  enableChangeHighlight?: boolean
  originalValue?: any

  // Select-specific props
  options?: SelectOption[]
  value?: any
  onChange?: (value: any) => void
}

const SelectField: React.FC<SelectFieldProps> = ({
  allowClear = false,
  loading = false,
  enableChangeHighlight = false,
  originalValue,
  options = [],
  value = '',
  onChange,
  sx,
  InputProps,
  ...textFieldProps
}) => {
  // State for tracking mouse hover
  const [isHovering, setIsHovering] = useState(false)

  // Check if the field value has changed
  const isChanged =
    enableChangeHighlight &&
    originalValue !== undefined &&
    originalValue?.toString() !== value?.toString()

  // Handle value change
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(event.target.value)
    }
  }

  // Handle clear button click
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation() // Prevent the dropdown menu from being triggered
    if (onChange) {
      onChange('')
    }
  }

  // Handle mouse hover events
  const handleMouseEnter = () => setIsHovering(true)
  const handleMouseLeave = () => setIsHovering(false)

  // Create clear button
  const clearButton =
    allowClear && value ? (
      <InputAdornment
        position='end'
        sx={{
          visibility: isHovering ? 'visible' : 'hidden',
          position: 'absolute',
          right: '28px', // Position to the left of the dropdown icon
          pointerEvents: 'auto' // Ensure it is clickable
        }}>
        <IconButton
          size='small'
          onClick={handleClear}
          sx={{
            padding: '2px',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)'
            }
          }}>
          <ClearIcon fontSize='small' />
        </IconButton>
      </InputAdornment>
    ) : undefined

  // Create loading indicator
  const loadingIndicator = loading ? (
    <InputAdornment
      position='end'
      sx={{
        position: 'absolute',
        right: allowClear ? '48px' : '28px', // Position based on clear button presence
        pointerEvents: 'none'
      }}>
      <CircularProgress size={16} />
    </InputAdornment>
  ) : undefined

  // Merge styles
  const mergedSx = {
    '& .MuiInputBase-root': {
      height: '36px'
    },
    '& .MuiSelect-select': {
      paddingRight: allowClear ? '48px' : '32px', // Reserve space for clear icon
      ...(isChanged && {
        color: 'secondary.dark',
        fontWeight: 'bold'
      })
    },
    ...(sx || {})
  }

  // Merge InputProps
  const mergedInputProps = {
    ...InputProps,
    endAdornment: (
      <>
        {loadingIndicator}
        {clearButton}
        {InputProps?.endAdornment}
      </>
    )
  }

  return (
    <TextField
      {...textFieldProps}
      select
      value={value}
      onChange={handleChange}
      disabled={textFieldProps.disabled || loading}
      sx={mergedSx}
      InputProps={mergedInputProps}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}>
      {options.map(option => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </TextField>
  )
}

export default SelectField
